/**
 * Comparison — Wave 3 (Credit-Repair Family) migration.
 *
 * Route: /comparison
 *
 * Rebuilt against the LandingV2 aesthetic bar. This is the sibling
 * analytical view of the credit-repair journey — it doesn't sit in
 * the letter-generation flow but reads directly off the same credit
 * report snapshots the letters are drafted from.
 *
 * Every previous `data-testid`, fetch (`/credit-reports/comparison/*`),
 * report snapshot render, chart composition, and empty-state gate is
 * preserved verbatim. Only the visual composition changes.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  FileUp,
  Info,
  Loader2,
  Minus,
  RefreshCw,
  Target,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import AppNav from "@/components/app/AppNav";
import ScoreHistoryChart from "@/components/app/ScoreHistoryChart";
import { Badge, Button } from "@/design-system";

const FIELD_LABEL = {
  balance: "Balance",
  status: "Status",
  past_due_amount: "Past due",
  credit_limit: "Credit limit",
  late_payment_counts: "Late payments",
  payment_history_summary: "Payment history",
  negative_remarks: "Negative remarks",
};

const TONE = {
  success: {
    fg:     "var(--ds-color-success)",
    bg:     "var(--ds-color-success-soft)",
    border: "var(--ds-color-success)",
  },
  warning: {
    fg:     "var(--ds-color-warning)",
    bg:     "var(--ds-color-warning-soft)",
    border: "var(--ds-color-warning)",
  },
  accent: {
    fg:     "var(--ds-color-accent)",
    bg:     "var(--ds-color-accent-soft)",
    border: "var(--ds-color-accent)",
  },
  neutral: {
    fg:     "var(--ds-color-ink-muted)",
    bg:     "var(--ds-color-surface-muted)",
    border: "var(--ds-color-border-strong)",
  },
};

export default function Comparison() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [comparison, setComparison] = useState(null);
  const [siteSettings, setSiteSettings] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recomputing, setRecomputing] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.get("/site-settings")
      .then((r) => { if (!cancelled) setSiteSettings(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!user?.funding_profile_complete) { setLoading(false); return undefined; }
    let cancelled = false;
    setLoading(true);
    Promise.allSettled([
      api.get("/credit-reports/comparison/latest"),
      api.get("/credit-reports/comparison/score-history"),
    ]).then(([cmpRes, histRes]) => {
      if (cancelled) return;
      if (cmpRes.status === "fulfilled") setComparison(cmpRes.value.data);
      else setError(formatApiError(cmpRes.reason));
      if (histRes.status === "fulfilled") setHistory(histRes.value.data || []);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user?.funding_profile_complete]);

  async function recompute() {
    setRecomputing(true);
    setError("");
    try {
      const r = await api.post("/credit-reports/comparison/recompute");
      setComparison(r.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setRecomputing(false);
    }
  }

  const insufficient = comparison?.status === "insufficient_data";

  if (!user?.funding_profile_complete) {
    return (
      <Shell disclaimer={siteSettings?.disclaimer_text}>
        <EmptyState
          title="Your Credit Progress"
          subtitle="Finish your profile to start tracking your credit progress over time."
          primaryLabel="Finish my profile"
          onPrimary={() => navigate("/profile")}
        />
      </Shell>
    );
  }

  if (loading) {
    return (
      <Shell disclaimer={siteSettings?.disclaimer_text}>
        <div
          data-testid="comparison-loading"
          className="flex items-center justify-center min-h-[40vh]"
        >
          <Loader2 className="w-6 h-6 animate-spin" style={{ color: "var(--ds-color-accent)" }} aria-hidden />
        </div>
      </Shell>
    );
  }

  if (insufficient) {
    return (
      <Shell disclaimer={siteSettings?.disclaimer_text}>
        <EmptyState
          title="Your Credit Progress"
          subtitle="Upload at least 2 credit reports (at least 30 days apart) to see your progress."
          primaryLabel="Upload a credit report"
          onPrimary={() => navigate("/credit-improvement")}
          extra={
            history.length >= 2 ? (
              <section
                data-testid="comparison-history-only"
                className="mt-10 text-left"
              >
                <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)] mb-3">
                  Your readiness score over time
                </div>
                <ScoreHistoryChart points={history} />
              </section>
            ) : null
          }
        />
      </Shell>
    );
  }

  const diff = comparison?.diff || {};
  const scoreDelta = diff.score_change?.delta;
  const readinessDelta = diff.readiness_change?.delta;

  return (
    <Shell disclaimer={siteSettings?.disclaimer_text}>
      <Link
        to="/credit-improvement"
        data-testid="comparison-back-link"
        className="inline-flex items-center gap-1.5 mt-6 text-[13px] font-medium text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)] transition-colors"
        style={{ transitionDuration: "var(--ds-duration-fast)" }}
      >
        ← Back to Credit Improvement
      </Link>

      {/* HERO */}
      <section
        data-testid="comparison-hero"
        className="mt-3 rounded-[var(--ds-radius-xl)] p-7 md:p-10"
        style={{
          background: "var(--ds-color-surface)",
          border: "1px solid var(--ds-color-border)",
          boxShadow: "var(--ds-shadow-card)",
        }}
      >
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--ds-color-ink-muted)]">
              Credit Repair · Progress
            </div>
            <h1
              className="mt-2 text-[var(--ds-color-ink)] leading-tight"
              style={{
                fontFamily: "var(--ds-font-display)",
                fontWeight: 600,
                letterSpacing: "-0.02em",
                fontSize: "clamp(2rem, 3.4vw, 2.75rem)",
              }}
            >
              Where you&rsquo;ve moved since your last report.
            </h1>
            <p className="mt-3 text-[14px] leading-relaxed text-[var(--ds-color-ink-muted)] max-w-2xl">
              Educational comparison only — credit bureau data can lag. We
              don&rsquo;t guarantee score increases or item removals.
            </p>
          </div>
          <Button
            variant="primary"
            size="md"
            data-testid="comparison-recompute-btn"
            onClick={recompute}
            disabled={recomputing}
            loading={recomputing}
          >
            {recomputing ? (
              <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
            ) : (
              <RefreshCw className="w-4 h-4" aria-hidden />
            )}
            {recomputing ? "Recomputing…" : "Recompute"}
          </Button>
        </div>

        <div className="mt-7 grid md:grid-cols-2 gap-4">
          <ReportCard
            label="Previous report"
            bureau={comparison?.from_bureau}
            date={comparison?.from_report_date}
            score={diff.score_change?.from}
            testId="comparison-report-from"
          />
          <ReportCard
            label="Latest report"
            bureau={comparison?.to_bureau}
            date={comparison?.to_report_date}
            score={diff.score_change?.to}
            testId="comparison-report-to"
          />
        </div>

        <div className="mt-7 grid md:grid-cols-2 gap-4">
          <DeltaCard
            label="Credit score change"
            delta={scoreDelta}
            fromValue={diff.score_change?.from}
            toValue={diff.score_change?.to}
            unit="pts"
            testId="comparison-score-delta"
          />
          <DeltaCard
            label="Readiness score change"
            delta={readinessDelta}
            fromValue={diff.readiness_change?.from_score}
            toValue={diff.readiness_change?.to_score}
            unit="pts"
            testId="comparison-readiness-delta"
            secondary
          />
        </div>

        {error && (
          <p
            data-testid="comparison-error"
            className="mt-4 text-[13px]"
            style={{ color: "var(--ds-color-danger)" }}
          >
            {error}
          </p>
        )}
      </section>

      {/* WARNINGS */}
      {(diff.warnings || []).length > 0 && (
        <section
          data-testid="comparison-warnings"
          className="mt-6 rounded-[var(--ds-radius-lg)] p-5"
          style={{ background: TONE.warning.bg, border: `1px solid ${TONE.warning.border}` }}
        >
          <div className="flex gap-3">
            <AlertTriangle className="w-4 h-4 mt-0.5" style={{ color: TONE.warning.fg }} aria-hidden />
            <div className="flex-1">
              <div
                className="text-[11px] font-semibold uppercase tracking-[0.14em]"
                style={{ color: TONE.warning.fg }}
              >
                Heads up
              </div>
              <ul className="mt-2 space-y-1.5 text-[13px] leading-relaxed text-[var(--ds-color-ink)]">
                {diff.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* CHART */}
      {history.length >= 2 && (
        <section data-testid="comparison-chart-section" className="mt-8">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)] mb-3">
            Score history
          </div>
          <div
            className="rounded-[var(--ds-radius-lg)] p-4"
            style={{
              background: "var(--ds-color-surface)",
              border: "1px solid var(--ds-color-border)",
              boxShadow: "var(--ds-shadow-card)",
            }}
          >
            <ScoreHistoryChart points={history} />
          </div>
        </section>
      )}

      {/* IMPROVED */}
      <BulletCard
        testId="comparison-improved"
        title="What improved"
        icon={TrendingUp}
        tone="success"
        items={diff.summary?.improved}
        emptyText="No improvements detected this period."
      />
      {/* WORSENED */}
      <BulletCard
        testId="comparison-worsened"
        title="What got worse"
        icon={TrendingDown}
        tone="warning"
        items={diff.summary?.worsened}
        emptyText="Nothing got worse — good work."
      />
      {/* UNCHANGED */}
      <BulletCard
        testId="comparison-unchanged"
        title="What stayed the same"
        icon={Minus}
        tone="neutral"
        items={diff.summary?.unchanged}
        emptyText=""
      />
      {/* NEXT ACTIONS */}
      <BulletCard
        testId="comparison-next-actions"
        title="Next best actions"
        icon={Target}
        tone="accent"
        items={diff.summary?.next_actions}
        emptyText=""
        ordered
      />

      {/* DETAILED ACCORDIONS */}
      <section data-testid="comparison-detail" className="mt-10 space-y-3">
        <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)] mb-3">
          Account-level changes
        </div>
        <DetailRow
          testId="comparison-detail-removed-neg"
          title={`Negative items dropped off (${(diff.accounts?.removed || []).filter((a) => a.is_negative).length})`}
          tone="success"
          icon={CheckCircle2}
          items={(diff.accounts?.removed || []).filter((a) => a.is_negative)}
          renderItem={(a, i) => (
            <AccountLine
              key={i}
              acct={a}
              note={a.account_status ? `Was: ${a.account_status}` : ""}
            />
          )}
        />
        <DetailRow
          testId="comparison-detail-added-neg"
          title={`New negative items (${(diff.accounts?.added_negative || []).length})`}
          tone="warning"
          icon={AlertTriangle}
          items={diff.accounts?.added_negative || []}
          renderItem={(a, i) => (
            <AccountLine
              key={i}
              acct={a}
              note={a.account_status ? `Now: ${a.account_status}` : ""}
              cta={{ label: "Review in Improvement Center", to: "/credit-improvement" }}
            />
          )}
        />
        <DetailRow
          testId="comparison-detail-updated"
          title={`Accounts updated (${(diff.accounts?.updated || []).length})`}
          tone="accent"
          icon={ArrowUpRight}
          items={diff.accounts?.updated || []}
          renderItem={(upd, i) => <AccountUpdateLine key={i} update={upd} />}
        />
        <DetailRow
          testId="comparison-detail-inquiries"
          title={`Inquiries · +${(diff.inquiries?.added || []).length} / -${(diff.inquiries?.removed || []).length}`}
          tone="accent"
          icon={ArrowUpRight}
          items={[
            ...(diff.inquiries?.added || []).map((x) => ({ ...x, _kind: "added" })),
            ...(diff.inquiries?.removed || []).map((x) => ({ ...x, _kind: "removed" })),
          ]}
          renderItem={(inq, i) => <InquiryLine key={i} inq={inq} />}
        />
        <DetailRow
          testId="comparison-detail-public-records"
          title={`Public records · +${(diff.public_records?.added || []).length} / -${(diff.public_records?.removed || []).length}`}
          tone="warning"
          icon={AlertTriangle}
          items={[
            ...(diff.public_records?.added || []).map((x) => ({ ...x, _kind: "added" })),
            ...(diff.public_records?.removed || []).map((x) => ({ ...x, _kind: "removed" })),
          ]}
          renderItem={(rec, i) => <PublicRecordLine key={i} rec={rec} />}
        />
      </section>

      {/* BOTTOM CTAs */}
      <section className="mt-10 grid md:grid-cols-2 gap-3">
        <BottomCta
          to="/credit-improvement"
          testId="comparison-cta-upload"
          Icon={FileUp}
          title="Upload a new credit report"
          description="We'll compare it to your latest report as soon as extraction completes."
        />
        <BottomCta
          to="/readiness"
          testId="comparison-cta-readiness"
          Icon={Target}
          title="View readiness details"
          description="See your latest readiness breakdown and the assessment time series in detail."
        />
      </section>
    </Shell>
  );
}

/* --------------------- Small components ------------------------------- */

function ReportCard({ label, bureau, date, score, testId }) {
  const dt = (() => {
    if (!date) return "—";
    try {
      return new Date(date).toLocaleDateString();
    } catch (e) {
      return String(date);
    }
  })();
  return (
    <div
      data-testid={testId}
      className="rounded-[var(--ds-radius-md)] p-5"
      style={{
        background: "var(--ds-color-surface-muted)",
        border: "1px solid var(--ds-color-border)",
      }}
    >
      <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]">
        {label}
      </div>
      <div className="mt-1 text-[var(--ds-color-ink)]">
        <span
          className="font-medium"
          style={{ fontFamily: "var(--ds-font-display)", fontWeight: 600, fontSize: "1.125rem" }}
        >
          {bureau || "Bureau unknown"}
        </span>{" "}
        · <span className="text-[13px] text-[var(--ds-color-ink-muted)]">{dt}</span>
      </div>
      <div
        className="mt-2 tabular-nums"
        style={{
          color: "var(--ds-color-accent)",
          fontFamily: "var(--ds-font-display)",
          fontWeight: 600,
          fontSize: "1.875rem",
          letterSpacing: "-0.02em",
        }}
      >
        {score ?? "—"}
      </div>
    </div>
  );
}

function DeltaCard({ label, delta, fromValue, toValue, unit, testId, secondary }) {
  const has = delta !== null && delta !== undefined;
  const newlyAvailable = !has && fromValue == null && toValue != null;
  const positive = has && delta > 0;
  const negative = has && delta < 0;
  const toneKey = positive ? "success" : negative ? "warning" : newlyAvailable ? "accent" : "neutral";
  const tone = TONE[toneKey];
  const Icon = positive ? TrendingUp : negative ? TrendingDown : Minus;
  const sign = positive ? "+" : negative ? "-" : "";
  return (
    <div
      data-testid={testId}
      className="rounded-[var(--ds-radius-md)] p-5"
      style={{
        background: "var(--ds-color-surface-muted)",
        border: `1px solid ${tone.border}`,
        opacity: secondary ? 0.95 : 1,
      }}
    >
      <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]">
        {label}
      </div>
      {newlyAvailable ? (
        <div className="mt-2">
          <div
            className="tabular-nums"
            style={{
              color: tone.fg,
              fontFamily: "var(--ds-font-display)",
              fontWeight: 600,
              fontSize: "1.5rem",
              letterSpacing: "-0.02em",
            }}
          >
            {toValue}{" "}
            <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]">
              {unit}
            </span>
          </div>
          <div
            data-testid={`${testId}-newly-available`}
            className="mt-1 text-[12px] text-[var(--ds-color-ink-muted)]"
          >
            Newly available — no prior score to compare.
          </div>
        </div>
      ) : (
        <div className="mt-2 flex items-baseline gap-3">
          <span
            className="tabular-nums"
            style={{
              color: tone.fg,
              fontFamily: "var(--ds-font-display)",
              fontWeight: 600,
              fontSize: "2.25rem",
              letterSpacing: "-0.02em",
            }}
          >
            {has ? `${sign}${Math.abs(delta)}` : "—"}
          </span>
          {has && (
            <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]">
              {unit}
            </span>
          )}
          <Icon className="w-5 h-5" style={{ color: tone.fg }} aria-hidden />
        </div>
      )}
    </div>
  );
}

function BulletCard({ testId, title, icon: Icon, tone: toneKey, items, emptyText, ordered }) {
  const hasItems = items && items.length > 0;
  if (!hasItems && !emptyText) return null;
  const tone = TONE[toneKey];
  return (
    <section
      data-testid={testId}
      className="mt-6 rounded-[var(--ds-radius-lg)] p-6 md:p-7"
      style={{
        background: "var(--ds-color-surface)",
        border: "1px solid var(--ds-color-border)",
        boxShadow: "var(--ds-shadow-card)",
      }}
    >
      <div className="flex items-center gap-3">
        <span
          className="inline-flex items-center justify-center w-9 h-9 rounded-[var(--ds-radius-md)] flex-shrink-0"
          style={{ background: tone.bg, color: tone.fg }}
        >
          <Icon className="w-4 h-4" aria-hidden />
        </span>
        <h3
          className="text-[var(--ds-color-ink)]"
          style={{ fontFamily: "var(--ds-font-display)", fontWeight: 600, fontSize: "1.0625rem" }}
        >
          {title}
        </h3>
      </div>
      {hasItems ? (
        ordered ? (
          <ol className="mt-4 space-y-2.5 list-decimal pl-5 text-[13px] leading-relaxed text-[var(--ds-color-ink)]">
            {items.map((s, i) => <li key={i} className="pl-1">{s}</li>)}
          </ol>
        ) : (
          <ul className="mt-4 space-y-2 text-[13px] leading-relaxed text-[var(--ds-color-ink)]">
            {items.map((s, i) => (
              <li key={i} className="flex gap-2.5 items-start">
                <span
                  className="mt-2 w-1.5 h-1.5 rounded-[var(--ds-radius-pill)] flex-shrink-0"
                  style={{ background: tone.fg }}
                />
                <span>{s}</span>
              </li>
            ))}
          </ul>
        )
      ) : (
        <p className="mt-3 text-[13px] text-[var(--ds-color-ink-muted)]">{emptyText}</p>
      )}
    </section>
  );
}

function DetailRow({ testId, title, tone: toneKey, icon: Icon, items, renderItem }) {
  const [open, setOpen] = useState(false);
  const isEmpty = !items || items.length === 0;
  const tone = TONE[toneKey];
  return (
    <div
      data-testid={testId}
      className="rounded-[var(--ds-radius-lg)]"
      style={{
        background: "var(--ds-color-surface)",
        border: "1px solid var(--ds-color-border)",
        boxShadow: "var(--ds-shadow-card)",
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-5 py-4 flex items-center gap-3 text-left disabled:cursor-default"
        disabled={isEmpty}
      >
        <span
          className="inline-flex items-center justify-center w-7 h-7 rounded-[var(--ds-radius-md)] flex-shrink-0"
          style={{ background: tone.bg, color: tone.fg }}
        >
          <Icon className="w-3.5 h-3.5" aria-hidden />
        </span>
        <span className="flex-1 text-[14px] font-medium text-[var(--ds-color-ink)]">{title}</span>
        {!isEmpty && (open ? (
          <ChevronUp className="w-4 h-4 text-[var(--ds-color-ink-muted)]" aria-hidden />
        ) : (
          <ChevronDown className="w-4 h-4 text-[var(--ds-color-ink-muted)]" aria-hidden />
        ))}
        {isEmpty && <Badge tone="neutral">None</Badge>}
      </button>
      {open && !isEmpty && (
        <div
          className="px-5 pb-5 space-y-3 border-t"
          style={{ borderColor: "var(--ds-color-border)" }}
        >
          <div className="pt-4">{items.map(renderItem)}</div>
        </div>
      )}
    </div>
  );
}

function AccountLine({ acct, note, cta }) {
  return (
    <div
      className="rounded-[var(--ds-radius-md)] p-4"
      style={{ background: "var(--ds-color-surface-muted)", border: "1px solid var(--ds-color-border)" }}
    >
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div
          className="text-[13px] text-[var(--ds-color-ink)]"
          style={{ fontFamily: "var(--ds-font-display)", fontWeight: 600, fontSize: "0.9375rem" }}
        >
          {acct.creditor_name || "Unknown creditor"}
          {acct.account_number_masked && (
            <span className="ml-2 text-[12px] font-normal text-[var(--ds-color-ink-muted)]">
              {acct.account_number_masked}
            </span>
          )}
        </div>
        <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]">
          {acct.account_type || acct.bureau_reporting || ""}
        </span>
      </div>
      {note && <p className="mt-1 text-[12px] text-[var(--ds-color-ink-muted)]">{note}</p>}
      {cta && (
        <Link
          to={cta.to}
          className="mt-3 inline-flex items-center gap-1 text-[12px] font-semibold hover:underline transition-colors"
          style={{ color: "var(--ds-color-accent)", transitionDuration: "var(--ds-duration-fast)" }}
        >
          {cta.label} <ArrowRight className="w-3 h-3" aria-hidden />
        </Link>
      )}
    </div>
  );
}

function _formatVal(field, v) {
  if (v == null) return "—";
  if (field === "balance" || field === "past_due_amount" || field === "credit_limit") {
    return `$${Number(v).toLocaleString()}`;
  }
  if (field === "late_payment_counts" && typeof v === "object") {
    const total = ["30", "60", "90", "120"].reduce((a, k) => a + (v[k] || 0), 0);
    return total === 0 ? "none" : `${total} late`;
  }
  if (field === "negative_remarks" && Array.isArray(v)) {
    return v.length ? v.join(", ") : "none";
  }
  return String(v);
}

function AccountUpdateLine({ update }) {
  const acct = update.new || update.old;
  return (
    <div
      className="rounded-[var(--ds-radius-md)] p-4"
      style={{ background: "var(--ds-color-surface-muted)", border: "1px solid var(--ds-color-border)" }}
    >
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div
          className="text-[var(--ds-color-ink)]"
          style={{ fontFamily: "var(--ds-font-display)", fontWeight: 600, fontSize: "0.9375rem" }}
        >
          {acct.creditor_name || "Unknown creditor"}
          {acct.account_number_masked && (
            <span className="ml-2 text-[12px] font-normal text-[var(--ds-color-ink-muted)]">
              {acct.account_number_masked}
            </span>
          )}
        </div>
        {update.match_confidence === "low" && (
          <Badge tone="warning" size="sm">Low confidence</Badge>
        )}
      </div>
      <ul className="mt-2 space-y-1 text-[12px] text-[var(--ds-color-ink-muted)]">
        {(update.changes || []).map((ch, i) => (
          <li key={i}>
            <span>{FIELD_LABEL[ch.field] || ch.field}:</span>{" "}
            <span className="text-[var(--ds-color-ink)]">{_formatVal(ch.field, ch.from)}</span>{" "}
            → <span className="text-[var(--ds-color-ink)]">{_formatVal(ch.field, ch.to)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function InquiryLine({ inq }) {
  const added = inq._kind === "added";
  return (
    <div
      className="rounded-[var(--ds-radius-md)] p-4 flex items-baseline justify-between gap-3 flex-wrap"
      style={{ background: "var(--ds-color-surface-muted)", border: "1px solid var(--ds-color-border)" }}
    >
      <div
        className="text-[var(--ds-color-ink)]"
        style={{ fontFamily: "var(--ds-font-display)", fontWeight: 600, fontSize: "0.9375rem" }}
      >
        {inq.creditor_name || "Unknown"}{" "}
        · <span className="text-[12px] font-normal text-[var(--ds-color-ink-muted)]">{inq.inquiry_date || "—"}</span>
      </div>
      <Badge tone={added ? "warning" : "success"} size="sm">
        {added ? "Added" : "Removed"}
      </Badge>
    </div>
  );
}

function PublicRecordLine({ rec }) {
  const added = rec._kind === "added";
  return (
    <div
      className="rounded-[var(--ds-radius-md)] p-4 flex items-baseline justify-between gap-3 flex-wrap"
      style={{ background: "var(--ds-color-surface-muted)", border: "1px solid var(--ds-color-border)" }}
    >
      <div
        className="text-[var(--ds-color-ink)]"
        style={{ fontFamily: "var(--ds-font-display)", fontWeight: 600, fontSize: "0.9375rem" }}
      >
        {rec.type || "Public record"}{" "}
        · <span className="text-[12px] font-normal text-[var(--ds-color-ink-muted)]">{rec.filing_date || "—"}</span>
        {rec.amount != null && (
          <span className="ml-2 text-[12px] font-normal text-[var(--ds-color-ink-muted)]">
            ${Number(rec.amount).toLocaleString()}
          </span>
        )}
      </div>
      <Badge tone={added ? "warning" : "success"} size="sm">
        {added ? "Added" : "Removed"}
      </Badge>
    </div>
  );
}

function BottomCta({ to, testId, Icon, title, description }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="rounded-[var(--ds-radius-lg)] p-6 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ds-color-focus)] focus-visible:ring-offset-2"
      style={{
        background: "var(--ds-color-surface)",
        border: "1px solid var(--ds-color-border)",
        boxShadow: "var(--ds-shadow-card)",
        transitionDuration: "var(--ds-duration-fast)",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = "var(--ds-color-border-strong)")}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--ds-color-border)")}
    >
      <div className="flex items-start gap-3">
        <Icon
          className="w-5 h-5 mt-1 flex-shrink-0"
          style={{ color: "var(--ds-color-accent)" }}
          aria-hidden
        />
        <div>
          <div
            className="text-[var(--ds-color-ink)]"
            style={{ fontFamily: "var(--ds-font-display)", fontWeight: 600, fontSize: "1rem" }}
          >
            {title}
          </div>
          <p className="mt-1 text-[13px] leading-relaxed text-[var(--ds-color-ink-muted)]">
            {description}
          </p>
        </div>
      </div>
    </Link>
  );
}

/* --------------------- Shell + EmptyState ----------------------------- */

function Shell({ children, disclaimer }) {
  return (
    <div
      data-testid="comparison-root"
      data-page-outer="wave-3"
      className="min-h-screen text-[var(--ds-color-ink)] bg-[var(--ds-color-background)]"
    >
      <AppNav />
      <main className="max-w-5xl mx-auto px-4 md:px-8 pt-10 pb-24">
        {disclaimer && (
          <div
            data-testid="comparison-disclaimer"
            className="rounded-[var(--ds-radius-md)] p-4 flex gap-3"
            style={{
              background: "var(--ds-color-surface-muted)",
              border: "1px solid var(--ds-color-border)",
            }}
          >
            <Info
              className="w-4 h-4 mt-0.5 flex-shrink-0"
              style={{ color: "var(--ds-color-ink-muted)" }}
              aria-hidden
            />
            <p className="text-[12px] leading-relaxed text-[var(--ds-color-ink-muted)]">{disclaimer}</p>
          </div>
        )}
        {children}
      </main>
    </div>
  );
}

function EmptyState({ title, subtitle, primaryLabel, onPrimary, extra }) {
  return (
    <section
      data-testid="comparison-empty"
      className="mt-6 rounded-[var(--ds-radius-lg)] p-10 md:p-14 text-center"
      style={{
        background: "var(--ds-color-surface)",
        border: "1px solid var(--ds-color-border)",
        boxShadow: "var(--ds-shadow-card)",
      }}
    >
      <div
        className="inline-flex items-center justify-center w-14 h-14 rounded-[var(--ds-radius-lg)] mx-auto"
        style={{ background: "var(--ds-color-accent-soft)", color: "var(--ds-color-accent)" }}
      >
        <TrendingUp className="w-6 h-6" aria-hidden />
      </div>
      <h2
        className="mt-5 text-[var(--ds-color-ink)]"
        style={{
          fontFamily: "var(--ds-font-display)",
          fontWeight: 600,
          letterSpacing: "-0.02em",
          fontSize: "clamp(1.5rem, 2.4vw, 1.875rem)",
        }}
      >
        {title}
      </h2>
      <p className="mt-3 text-[14px] leading-relaxed text-[var(--ds-color-ink-muted)] max-w-xl mx-auto">
        {subtitle}
      </p>
      <div className="mt-6 flex justify-center">
        <Button
          variant="primary"
          size="md"
          onClick={onPrimary}
          data-testid="comparison-empty-cta"
        >
          {primaryLabel} <ArrowRight className="w-4 h-4" aria-hidden />
        </Button>
      </div>
      {extra}
    </section>
  );
}
